import { useEffect, useRef, useState } from 'react';

/**
 * Hover opens the menu temporarily; click pins it open until an outside click.
 * @param {{ hoverCloseDelay?: number }} options - ms to wait before closing on mouse leave (unpinned only)
 */
export function useHelpMenuTrigger({ hoverCloseDelay = 0 } = {}) {
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isOpen = isPinned || isHovered;

  const isTargetInside = (target) => {
    if (!(target instanceof Node)) return false;
    return (
      containerRef.current?.contains(target) ||
      panelRef.current?.contains(target)
    );
  };

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const onMouseEnter = () => {
    clearCloseTimer();
    setIsHovered(true);
  };

  const onMouseLeave = (e) => {
    if (isTargetInside(e?.relatedTarget)) return;

    if (hoverCloseDelay > 0 && !isPinned) {
      clearCloseTimer();
      closeTimerRef.current = setTimeout(() => {
        setIsHovered(false);
        closeTimerRef.current = null;
      }, hoverCloseDelay);
      return;
    }

    setIsHovered(false);
  };

  const onClick = (e) => {
    e?.stopPropagation?.();
    clearCloseTimer();
    setIsPinned(true);
  };

  const close = () => {
    clearCloseTimer();
    setIsPinned(false);
    setIsHovered(false);
  };

  useEffect(() => () => clearCloseTimer(), []);

  useEffect(() => {
    if (!isPinned) return undefined;

    const handleClickOutside = (event) => {
      if (!isTargetInside(event.target)) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPinned]);

  return {
    containerRef,
    panelRef,
    isOpen,
    isPinned,
    isActive: isPinned,
    onMouseEnter,
    onMouseLeave,
    onClick,
    close,
  };
}
